import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  alpha,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  Add as AddIcon,
  CheckCircleOutline as PaidIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
  HandshakeOutlined as CommissionIcon,
  HourglassTopOutlined as PendingIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { format } from 'date-fns';

import commissionService from '../services/commissionService';
import { usePermissions } from '../context/PermissionsContext';

const EMPTY_FORM = {
  project_id: '',
  user_id: '',
  billing_document_id: '',
  sale_amount: '',
  commission_type: 'percentage',
  commission_rate: '',
  commission_amount: '',
  currency: 'MXN',
  commission_date: format(new Date(), 'yyyy-MM-dd'),
  notes: ''
};

const AUTOCOMPLETE_SLOT_PROPS = {
  paper: {
    elevation: 12,
    sx: {
      mt: 0.75,
      borderRadius: 2.25,
      border: '1px solid',
      borderColor: 'divider',
      overflow: 'hidden'
    }
  },
  listbox: {
    sx: {
      maxHeight: 288,
      py: 0.5,
      '& .MuiAutocomplete-option': {
        minHeight: 58,
        px: 1.5,
        py: 0.8,
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'divider'
      },
      '& .MuiAutocomplete-option:last-of-type': {
        borderBottom: 0
      }
    }
  }
};

const SELECT_MENU_PROPS = {
  PaperProps: {
    sx: {
      mt: 0.75,
      maxHeight: 288,
      borderRadius: 2.25,
      border: '1px solid',
      borderColor: 'divider'
    }
  }
};

const DropdownOption = ({ primary, secondary }) => (
  <Box sx={{ minWidth: 0, width: '100%' }}>
    <Typography variant="body2" noWrap sx={{ fontWeight: 750 }}>{primary}</Typography>
    {secondary && <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mt: 0.2 }}>{secondary}</Typography>}
  </Box>
);

const money = (value, currency = 'MXN') => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: currency || 'MXN'
}).format(Number(value || 0));

const dateLabel = (value) => {
  if (!value) return '-';
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  return Number.isNaN(date.getTime()) ? '-' : format(date, 'dd MMM yyyy');
};

export default function CommissionsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission('finance', 'create');
  const canEdit = hasPermission('finance', 'edit');
  const canDelete = hasPermission('finance', 'delete');
  const canPay = hasPermission('finance', 'approve') || canCreate;

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [options, setOptions] = useState({ users: [], projects: [], documents: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [commissionResponse, optionResponse] = await Promise.all([
        commissionService.getCommissions({ status, q: search.trim() || undefined }),
        commissionService.getOptions()
      ]);
      setRows(commissionResponse.commissions || []);
      setSummary(commissionResponse.summary || {});
      setOptions({
        users: optionResponse.users || [],
        projects: optionResponse.projects || [],
        documents: optionResponse.documents || []
      });
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.message || 'Unable to load commissions.');
    } finally {
      setLoading(false);
    }
  }, [search, status]);

  useEffect(() => {
    const timer = setTimeout(loadData, 250);
    return () => clearTimeout(timer);
  }, [loadData]);

  const selectedProject = useMemo(() => options.projects.find((item) => String(item.id) === String(form.project_id)) || null, [form.project_id, options.projects]);
  const selectedUser = useMemo(() => options.users.find((item) => String(item.id) === String(form.user_id)) || null, [form.user_id, options.users]);
  const selectedDocument = useMemo(() => options.documents.find((item) => String(item.id) === String(form.billing_document_id)) || null, [form.billing_document_id, options.documents]);
  const availableDocuments = useMemo(() => options.documents.filter((item) => !form.project_id || String(item.project_id) === String(form.project_id)), [form.project_id, options.documents]);
  const calculatedAmount = useMemo(() => {
    if (form.commission_type === 'fixed') return Number(form.commission_amount || 0);
    return Number(form.sale_amount || 0) * Number(form.commission_rate || 0) / 100;
  }, [form.commission_amount, form.commission_rate, form.commission_type, form.sale_amount]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (commission) => {
    setEditing(commission);
    setForm({
      project_id: String(commission.project_id || ''),
      user_id: String(commission.user_id || ''),
      billing_document_id: String(commission.billing_document_id || ''),
      sale_amount: String(commission.sale_amount || ''),
      commission_type: commission.commission_type || 'percentage',
      commission_rate: commission.commission_rate === null ? '' : String(commission.commission_rate),
      commission_amount: String(commission.commission_amount || ''),
      currency: commission.currency || 'MXN',
      commission_date: String(commission.commission_date || '').slice(0, 10),
      notes: commission.notes || ''
    });
    setDialogOpen(true);
  };

  const saveCommission = async () => {
    if (!form.project_id || !form.user_id || calculatedAmount <= 0) return;
    try {
      setBusy(true);
      if (editing) await commissionService.updateCommission(editing.id, form);
      else await commissionService.createCommission(form);
      setDialogOpen(false);
      setSnackbar({ open: true, severity: 'success', message: editing ? 'Commission updated.' : 'Commission created.' });
      await loadData();
    } catch (requestError) {
      setSnackbar({ open: true, severity: 'error', message: requestError.response?.data?.message || requestError.message });
    } finally {
      setBusy(false);
    }
  };

  const markPaid = async (commission) => {
    if (!window.confirm(`Mark ${money(commission.commission_amount, commission.currency)} for ${commission.salesperson_name} as paid? This will create a Finance expense.`)) return;
    try {
      setBusy(true);
      await commissionService.markPaid(commission.id, format(new Date(), 'yyyy-MM-dd'));
      setSnackbar({ open: true, severity: 'success', message: 'Commission paid and expense recorded.' });
      await loadData();
    } catch (requestError) {
      setSnackbar({ open: true, severity: 'error', message: requestError.response?.data?.message || requestError.message });
    } finally {
      setBusy(false);
    }
  };

  const removeCommission = async (commission) => {
    if (!window.confirm(`Delete the pending commission for ${commission.salesperson_name}?`)) return;
    try {
      setBusy(true);
      await commissionService.deleteCommission(commission.id);
      setSnackbar({ open: true, severity: 'success', message: 'Commission deleted.' });
      await loadData();
    } catch (requestError) {
      setSnackbar({ open: true, severity: 'error', message: requestError.response?.data?.message || requestError.message });
    } finally {
      setBusy(false);
    }
  };

  const metrics = ['MXN', 'USD'].flatMap((currency) => [
    { key: `${currency}-pending`, label: `Pending (${currency})`, value: money(summary[currency]?.pending, currency), icon: PendingIcon, color: theme.palette.warning.main },
    { key: `${currency}-paid`, label: `Paid (${currency})`, value: money(summary[currency]?.paid, currency), icon: PaidIcon, color: theme.palette.success.main }
  ]);

  return (
    <Box sx={{ width: '100%', pb: 3 }}>
      <Paper
        sx={{
          p: { xs: 1.35, md: 1.6 },
          borderRadius: 2.5,
          color: 'common.white',
          backgroundImage: 'linear-gradient(120deg, #123866 0%, #1d63b8 58%, #0b8a94 100%)',
          boxShadow: `0 10px 24px ${alpha('#07162f', 0.2)}`
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} gap={1.25}>
          <Stack direction="row" spacing={1.1} alignItems="center">
            <Avatar sx={{ width: 42, height: 42, bgcolor: alpha('#fff', 0.16) }}><CommissionIcon sx={{ fontSize: 22 }} /></Avatar>
            <Box>
              <Typography variant="overline" sx={{ color: alpha('#fff', 0.72), fontWeight: 800 }}>SALES COMPENSATION</Typography>
              <Typography variant="h5" sx={{ fontWeight: 850, lineHeight: 1.1 }}>Commissions</Typography>
              <Typography variant="body2" sx={{ mt: 0.6, color: alpha('#fff', 0.82) }}>Track commissions by project and record their payment directly in Finance.</Typography>
            </Box>
          </Stack>
          {canCreate && <Button size="small" variant="contained" color="inherit" startIcon={<AddIcon />} onClick={openCreate} sx={{ color: '#123866', fontWeight: 800 }}>New Commission</Button>}
        </Stack>
      </Paper>

      <Grid container spacing={1.2} sx={{ mt: 0.25 }}>
        {metrics.map(({ key, label, value, icon: Icon, color }) => (
          <Grid item xs={12} sm={6} lg={3} key={key}>
            <Card variant="outlined" sx={{ borderRadius: 2.5, borderColor: alpha(color, 0.28) }}>
              <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.35, '&:last-child': { pb: 1.35 } }}>
                <Box><Typography variant="caption" color="text.secondary" sx={{ fontWeight: 750 }}>{label}</Typography><Typography variant="h6" sx={{ mt: 0.4, fontWeight: 850, color }}>{value}</Typography></Box>
                <Avatar sx={{ bgcolor: alpha(color, 0.12), color }}><Icon /></Avatar>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper variant="outlined" sx={{ mt: 1.5, p: 1.3, borderRadius: 2.5 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
          <TextField fullWidth size="small" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search salesperson, project, or document..." InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }} />
          <TextField select size="small" label="Status" value={status} onChange={(event) => setStatus(event.target.value)} sx={{ minWidth: { xs: 0, sm: 170 }, width: { xs: '100%', sm: 'auto' } }}>
            <MenuItem value="all">All commissions</MenuItem><MenuItem value="pending">Pending</MenuItem><MenuItem value="paid">Paid</MenuItem><MenuItem value="cancelled">Cancelled</MenuItem>
          </TextField>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      <Stack spacing={1.2} sx={{ display: { xs: 'flex', md: 'none' }, mt: 1.5 }}>
        {loading ? <Box sx={{ py: 7, textAlign: 'center' }}><CircularProgress /></Box> : rows.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2.5 }}><CommissionIcon sx={{ fontSize: 44, color: 'text.disabled' }} /><Typography color="text.secondary">No commissions match this view.</Typography></Paper>
        ) : rows.map((commission) => (
          <Paper key={`mobile-commission-${commission.id}`} variant="outlined" sx={{ p: 1.5, borderRadius: 2.5 }}>
            <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
              <Box sx={{ minWidth: 0 }}><Typography variant="subtitle2" fontWeight={850} sx={{ overflowWrap: 'anywhere' }}>{commission.salesperson_name}</Typography><Typography variant="caption" color="text.secondary">{dateLabel(commission.commission_date)}</Typography></Box>
              <Chip size="small" label={commission.status === 'paid' ? 'Paid' : commission.status === 'cancelled' ? 'Cancelled' : 'Pending'} color={commission.status === 'paid' ? 'success' : commission.status === 'cancelled' ? 'default' : 'warning'} variant="outlined" />
            </Stack>
            <Typography variant="body2" fontWeight={750} sx={{ mt: 1.2, overflowWrap: 'anywhere' }}>{commission.project_name}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{commission.invoice_number || 'No sales document linked'}</Typography>
            <Box sx={{ mt: 1.2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              <Box><Typography variant="caption" color="text.secondary">Sale</Typography><Typography variant="body2" fontWeight={750}>{money(commission.sale_amount, commission.currency)}</Typography></Box>
              <Box sx={{ textAlign: 'right' }}><Typography variant="caption" color="text.secondary">Commission</Typography><Typography variant="body2" fontWeight={900} color="primary.main">{money(commission.commission_amount, commission.currency)}</Typography></Box>
            </Box>
            <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 1.3 }}>
              {commission.status === 'pending' && canPay && <Button size="small" variant="contained" color="success" startIcon={<PaidIcon />} onClick={() => markPaid(commission)}>Pay</Button>}
              {commission.status === 'pending' && canEdit && <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => openEdit(commission)}>Edit</Button>}
              {commission.status === 'pending' && canDelete && <IconButton size="small" color="error" onClick={() => removeCommission(commission)}><DeleteIcon fontSize="small" /></IconButton>}
              {commission.expense_id && <Chip size="small" label={`Expense #${commission.expense_id}`} variant="outlined" />}
            </Stack>
          </Paper>
        ))}
      </Stack>
      <TableContainer component={Paper} variant="outlined" sx={{ display: { xs: 'none', md: 'block' }, mt: 1.5, borderRadius: 2.5 }}>
        <Table size="small">
          <TableHead><TableRow><TableCell>Salesperson</TableCell><TableCell>Project / Document</TableCell><TableCell>Sale</TableCell><TableCell>Commission</TableCell><TableCell>Status</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={6} align="center" sx={{ py: 7 }}><CircularProgress /></TableCell></TableRow> : rows.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 7 }}><CommissionIcon sx={{ fontSize: 44, color: 'text.disabled' }} /><Typography color="text.secondary">No commissions match this view.</Typography></TableCell></TableRow>
            ) : rows.map((commission) => (
              <TableRow key={commission.id} hover>
                <TableCell><Typography variant="body2" sx={{ fontWeight: 800 }}>{commission.salesperson_name}</Typography><Typography variant="caption" color="text.secondary">{dateLabel(commission.commission_date)}</Typography></TableCell>
                <TableCell><Typography variant="body2" sx={{ fontWeight: 700 }}>{commission.project_name}</Typography><Typography variant="caption" color="text.secondary">{commission.invoice_number || 'No sales document linked'}</Typography></TableCell>
                <TableCell><Typography variant="body2" sx={{ fontWeight: 750 }}>{money(commission.sale_amount, commission.currency)}</Typography></TableCell>
                <TableCell><Typography variant="body2" sx={{ fontWeight: 850, color: 'primary.main' }}>{money(commission.commission_amount, commission.currency)}</Typography><Typography variant="caption" color="text.secondary">{commission.commission_type === 'percentage' ? `${commission.commission_rate}%` : 'Fixed amount'}</Typography></TableCell>
                <TableCell><Chip size="small" label={commission.status === 'paid' ? 'Paid' : commission.status === 'cancelled' ? 'Cancelled' : 'Pending payment'} color={commission.status === 'paid' ? 'success' : commission.status === 'cancelled' ? 'default' : 'warning'} variant="outlined" /></TableCell>
                <TableCell align="right">
                  {commission.status === 'pending' && canPay && <Tooltip title="Mark as paid and create expense"><Button size="small" variant="contained" color="success" startIcon={<PaidIcon />} onClick={() => markPaid(commission)}>Pay</Button></Tooltip>}
                  {commission.status === 'pending' && canEdit && <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(commission)}><EditIcon fontSize="small" /></IconButton></Tooltip>}
                  {commission.status === 'pending' && canDelete && <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => removeCommission(commission)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>}
                  {commission.expense_id && <Chip size="small" label={`Expense #${commission.expense_id}`} variant="outlined" sx={{ ml: 0.5 }} />}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => !busy && setDialogOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>{editing ? 'Edit Commission' : 'New Commission'}</DialogTitle>
        <DialogContent dividers sx={{ pt: 2.5 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(12, minmax(0, 1fr))' },
              gap: 2,
              alignItems: 'start',
              '& > *': { minWidth: 0 }
            }}
          >
            <Box sx={{ gridColumn: { md: 'span 6' } }}>
              <Autocomplete
                autoHighlight
                openOnFocus
                options={options.projects}
                value={selectedProject}
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                slotProps={AUTOCOMPLETE_SLOT_PROPS}
                noOptionsText="No projects found"
                onChange={(event, value) => setForm((current) => ({ ...current, project_id: value ? String(value.id) : '', billing_document_id: '' }))}
                renderOption={(props, option) => {
                  const { key, ...optionProps } = props;
                  return <Box component="li" key={key} {...optionProps}><DropdownOption primary={option.name || `Project #${option.id}`} secondary={`Project #${option.id}`} /></Box>;
                }}
                renderInput={(params) => <TextField {...params} required label="Project" placeholder="Search a project..." />}
                sx={{ '& .MuiAutocomplete-inputRoot': { minWidth: 0 }, '& input': { textOverflow: 'ellipsis' } }}
              />
            </Box>

            <Box sx={{ gridColumn: { md: 'span 6' } }}>
              <Autocomplete
                autoHighlight
                openOnFocus
                options={options.users}
                value={selectedUser}
                getOptionLabel={(option) => option.name || option.email || ''}
                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                slotProps={AUTOCOMPLETE_SLOT_PROPS}
                noOptionsText="No salespeople found"
                onChange={(event, value) => setForm((current) => ({ ...current, user_id: value ? String(value.id) : '' }))}
                renderOption={(props, option) => {
                  const { key, ...optionProps } = props;
                  return <Box component="li" key={key} {...optionProps}><DropdownOption primary={option.name || option.email} secondary={option.email} /></Box>;
                }}
                renderInput={(params) => <TextField {...params} required label="Salesperson" placeholder="Search a team member..." />}
                sx={{ '& .MuiAutocomplete-inputRoot': { minWidth: 0 }, '& input': { textOverflow: 'ellipsis' } }}
              />
            </Box>

            <Box sx={{ gridColumn: { md: 'span 12' } }}>
              <Autocomplete
                autoHighlight
                openOnFocus
                options={availableDocuments}
                value={selectedDocument}
                getOptionLabel={(option) => `${option.invoice_number} / ${option.client_name || 'No client'} / ${money(option.total, option.currency)}`}
                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                slotProps={AUTOCOMPLETE_SLOT_PROPS}
                noOptionsText={form.project_id ? 'No documents linked to this project' : 'No documents found'}
                onChange={(event, value) => setForm((current) => ({ ...current, billing_document_id: value ? String(value.id) : '', project_id: value?.project_id ? String(value.project_id) : current.project_id, sale_amount: value ? String(value.total || '') : current.sale_amount, currency: value?.currency || current.currency }))}
                renderOption={(props, option) => {
                  const { key, ...optionProps } = props;
                  return <Box component="li" key={key} {...optionProps}><DropdownOption primary={`${option.invoice_number} - ${option.client_name || 'No client'}`} secondary={`${String(option.type || 'document').replace('_', ' ')} / ${money(option.total, option.currency)}`} /></Box>;
                }}
                renderInput={(params) => <TextField {...params} label="Linked Quote, PO, or Invoice (Optional)" placeholder="Search document number or client..." helperText="Selecting a document fills the sale amount and currency." />}
                sx={{ '& .MuiAutocomplete-inputRoot': { minWidth: 0 }, '& input': { textOverflow: 'ellipsis' } }}
              />
            </Box>

            <TextField sx={{ gridColumn: { md: 'span 4' } }} fullWidth required type="number" label="Sale Amount" value={form.sale_amount} onChange={(event) => setForm((current) => ({ ...current, sale_amount: event.target.value }))} inputProps={{ min: 0, step: '0.01' }} />
            <TextField sx={{ gridColumn: { md: 'span 4' } }} select fullWidth label="Currency" value={form.currency} onChange={(event) => setForm((current) => ({ ...current, currency: event.target.value }))} SelectProps={{ MenuProps: SELECT_MENU_PROPS }}><MenuItem value="MXN">MXN - Mexican Peso</MenuItem><MenuItem value="USD">USD - US Dollar</MenuItem></TextField>
            <TextField sx={{ gridColumn: { md: 'span 4' } }} fullWidth type="date" label="Commission Date" value={form.commission_date} onChange={(event) => setForm((current) => ({ ...current, commission_date: event.target.value }))} InputLabelProps={{ shrink: true }} />

            <TextField sx={{ gridColumn: { md: 'span 4' } }} select fullWidth label="Calculation" value={form.commission_type} onChange={(event) => setForm((current) => ({ ...current, commission_type: event.target.value }))} SelectProps={{ MenuProps: SELECT_MENU_PROPS }}><MenuItem value="percentage">Percentage of sale</MenuItem><MenuItem value="fixed">Fixed amount</MenuItem></TextField>
            <Box sx={{ gridColumn: { md: 'span 4' } }}>
              {form.commission_type === 'percentage'
                ? <TextField fullWidth required type="number" label="Commission %" value={form.commission_rate} onChange={(event) => setForm((current) => ({ ...current, commission_rate: event.target.value }))} inputProps={{ min: 0, step: '0.01' }} />
                : <TextField fullWidth required type="number" label="Commission Amount" value={form.commission_amount} onChange={(event) => setForm((current) => ({ ...current, commission_amount: event.target.value }))} inputProps={{ min: 0, step: '0.01' }} />}
            </Box>
            <Paper variant="outlined" sx={{ gridColumn: { md: 'span 4' }, minHeight: 56, px: 1.6, py: 1, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.06), display: 'flex', flexDirection: 'column', justifyContent: 'center' }}><Typography variant="caption" color="text.secondary">Amount owed</Typography><Typography variant="h6" color="primary.main" sx={{ fontWeight: 850, lineHeight: 1.2 }}>{money(calculatedAmount, form.currency)}</Typography></Paper>
            <TextField sx={{ gridColumn: { md: 'span 12' } }} fullWidth multiline minRows={3} label="Internal Notes" value={form.notes} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions><Button onClick={() => setDialogOpen(false)} disabled={busy}>Cancel</Button><Button variant="contained" onClick={saveCommission} disabled={busy || !form.project_id || !form.user_id || calculatedAmount <= 0}>{busy ? 'Saving...' : editing ? 'Update Commission' : 'Create Commission'}</Button></DialogActions>
      </Dialog>

      {busy && <Box sx={{ position: 'fixed', inset: 0, bgcolor: alpha('#000', 0.16), zIndex: theme.zIndex.modal + 2, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>}
      <Snackbar open={snackbar.open} autoHideDuration={4500} onClose={() => setSnackbar((current) => ({ ...current, open: false }))}><Alert severity={snackbar.severity} onClose={() => setSnackbar((current) => ({ ...current, open: false }))}>{snackbar.message}</Alert></Snackbar>
    </Box>
  );
}
